import React, {useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux';
import {getCookie} from 'typescript-cookie';
import {Col, Result, Row, Spin, Typography} from 'antd';
import StatisticCard from '../../components/molecules/Cards/StatisticCard/component';
import ActiveCampaignsGraph from './charts/campaignsOverview';
import ClicksGraph from './charts/clicksAndEarnings';
import UsersGraph from './charts/users';
import ClicksRateGraph from './charts/clickRate';
import ClicksAndBudgetGraph from './charts/campaignsBudgetAndClicks';
import PlotService from '../../services/plotService';
import * as statisticActions from '../../store/modules/plots/actions';
import {RootState} from '../../store/rootReducer';
import './style.css'
import ClicksOverTimeGraph from './charts/clicksOverTime';
import {useIsMobile} from "../../utils/helpers";

const {Title} = Typography;


const StatisticsPage = () => {
    const dispatch = useDispatch();
    const role = (getCookie('roleType') || '').toLowerCase();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let mounted = true;
        const fetchStats = async () => {
            setLoading(true);
            setError(null);
            try {
                const result = await PlotService.get_statistics();
                if (!mounted) return;
                dispatch(statisticActions.fetchStatisticDataSuccess(result.data));
            } catch (err: any) {
                if (!mounted) return;
                setError(err?.response?.data?.message || 'Could not get statistic data');
            } finally {
                if (mounted) setLoading(false);
            }
        };
        fetchStats();
        return () => {
            mounted = false;
        };
    }, [dispatch, role]);

    const statisticsByRole = useSelector((state: RootState) =>
        (state?.statistics?.data || []).filter(
            (e: { role: string }) => (e?.role || '').toLowerCase() === role
        )
    );

    const reloadStats = async (granularity: 'day' | 'month') => {
        const scrollY = window.scrollY;
        
        setLoading(true);
        const result = await PlotService.get_statistics(granularity);
        dispatch(statisticActions.fetchStatisticDataSuccess(result.data));
        setLoading(false);

        requestAnimationFrame(() => {
            window.scrollTo({ top: scrollY, behavior: 'auto' });
        });
    };

    const statistics_object_ = statisticsByRole[0]?.statistics || {};
    const {graphs, ...dashboard_statistics} = statistics_object_;
    const campaignsOverview = graphs?.campaignsOverview || []
    const clicksPerCampaign = graphs?.clicksPerCampaign || []
    const earningsAcrossCampaigns = graphs?.earningsAcrossCampaigns || []
    const budgetUtilizationPerCampaign = graphs?.budgetUtilizationPerCampaign || []
    const clicksGeographicalDistribution = graphs?.clicksGeographicalDistribution || []
    const clicksPerCampaignBudget = graphs?.clicksPerCampaignBudget || []
    const clicksPerClickRate = graphs?.clicksPerClickRate || []
    const promotersPerClickRate = graphs?.promotersPerClickRate || []
    const monthlyEarningsPerUserType = graphs?.monthlyEarningsPerUserType || []
    const onboardedUsersPerType = graphs?.onboardedUsersPerType || []
    const earningsOverTime = graphs?.earningsOverTime || { granularity: 'month', data: [] };
    const clicksOverTime = graphs?.clicksOverTime || { granularity: 'month', data: [] };

    const isMobile = useIsMobile();

    if (loading) {
        return (
            <div style={{minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                <Spin size="large"/>
            </div>
        );
    }

    if (error) {
        return (<Result status="warning" title="We couldn’t load your dashboard" subTitle={error}/>);
    }

    const statistics = Object.entries(dashboard_statistics).map(([key, value]) => {
        let formattedTitle;
        let info;

        if (key === 'numberOfCampaigns') {
            formattedTitle = 'Campaigns';
            info = (role === 'advertiser') ? 'Number of campaigns created by you' : 'Number of active campaigns you participate in';
        } else if (key === 'numberOfGeneratedClicks') {
            formattedTitle = 'Generated clicks';
            info = 'Number of clicks generated via your campaigns';
        } else if (key === 'numberOfPromoters') {
            formattedTitle = 'Promoters';
            info = 'Number of active promoters working on your campaigns';
        } else if (key === 'currentBalance') {
            formattedTitle = 'Current balance';
            info = 'Current balance sitting on your wallet';
        } else if (key === 'campaignBalance') {
            formattedTitle = 'Campaigns\' balance';
            info = 'Current balance available in your active campaigns';
        } else if (key === 'numberOfTotalClicksGenerated') {
            formattedTitle = 'Generated clicks';
            info = 'Number of clicks you generated in campaigns you participate in';
        } else if (key === 'earningsFromClicks') {
            formattedTitle = 'Earnings from clicks';
            info = 'Earnings from the clicks you generated in campaigns you participate in';
        } else if (key === 'numberOfOnboardedUsers') {
            formattedTitle = 'Onboarded users';
            info = 'Number of users you have onboarded on Promotify';
        } else if (key === 'onboardedUsersCommission') {
            formattedTitle = 'Earnings from onboardings';
            info = 'Earnings from the subscription fee commission you gain for users you have onboarded on Promotify';
        } else if (key === 'campaignDepositCommission') {
            formattedTitle = 'Earnings from users’ activity';
            info = 'Earnings from the commission you gain for the money being deposited by advertiser users you have onboarded on Promotify';
        } else {
            const formattedKey = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
            formattedTitle = formattedKey.split(' ').map((word, index) =>
                index === 0 ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : word.toLowerCase()
            ).join(' ');
            info = 'Add info here';
        }

        return {
            title: formattedTitle,
            value: typeof value === 'number' ? value : 0,
            info: info,
            symbol: info.toLowerCase().includes('earnings') || info.toLowerCase().includes('balance') ? '$' : ''
        };
    });

    return (
        <div className='stats-page'>
            <Row justify="center" className={'main-rows'}>
                <Col>
                    <Title level={1} style={{textAlign: 'center', marginTop: '20px', fontSize: isMobile ? '20px' : '30px'}}>
                        Analysis dashboard
                    </Title>
                </Col>
            </Row>
            <Row className="main-rows" gutter={[16, 16]}>
                {statistics.map((stat, index) => (
                    <Col key={index} xs={24} sm={12} md={12} lg={6} style={{display: 'flex'}}>
                        <StatisticCard
                            title={stat.title}
                            value={stat.value || 0}
                            symbol={stat.symbol}
                            info={stat.info}
                            className="statistic-header"
                        />
                    </Col>
                ))}
            </Row>

            {["promoter", "partner"].includes(role) && (
                <div className={'main-rows'}>
                    <ActiveCampaignsGraph
                        earningsOverTime={earningsOverTime}
                        campaignData={campaignsOverview}
                        onGranularityChange={reloadStats}
                    />
                </div>
            )}
            {role === 'advertiser' && (
                <div className={'main-rows'}>
                    <ClicksAndBudgetGraph
                        clicksGeographicalDistribution={clicksGeographicalDistribution}
                        budgetUtilizationPerCampaign={budgetUtilizationPerCampaign}
                        clicksPerCampaign={clicksPerCampaign}/>
                </div>
            )}
            {role === 'partner' && (
                <div className={'main-rows'}>
                    <UsersGraph onboardedUsersPerType={onboardedUsersPerType}
                                monthlyEarningsPerUserType={monthlyEarningsPerUserType}/>
                </div>
            )}
            {["promoter", "partner"].includes(role) && (
                <div className={'main-rows'}>
                    <ClicksGraph clicksPerCampaign={clicksPerCampaign}
                                 earningsAcrossCampaigns={earningsAcrossCampaigns}/>
                </div>
            )}
            {role === 'advertiser' && (
                <div className={'main-rows'}>
                    <ClicksRateGraph clicksPerCampaignBudget={clicksPerCampaignBudget}
                                     clicksPerClickRate={clicksPerClickRate}
                                     promotersPerClickRate={promotersPerClickRate}/>
                </div>
            )}
            <div className="main-rows">
                <ClicksOverTimeGraph
                    clicksOverTime={clicksOverTime}
                    onGranularityChange={reloadStats}
                />
            </div>
        </div>
    );
};

export default StatisticsPage;